import React, { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import {
  Typography,
  Table,
  Tabs,
  Icon,
  Spinner,
  Button,
  BaseIcon,
  TextField,
  Tag,
} from '@scale/scaleui';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  listJobConfigs,
  deleteJobConfig,
  updateJobConfig,
  deleteResumeScreeningJob,
  listResumeScreeningJobs,
  getJobConfigEvents,
  getResumeScreeningJobEvents,
} from 'react-scale/corp/lib/api/growth';
import {
  ResumeScreeningConfig,
  ResumeScreeningConfigStatus,
  UpdateJobConfigFields,
  ResumeScreeningJob,
  PodType,
  ScreenType,
  ScreenFilter,
} from '@scale/shared-types';
import FilterComponent from '../components/FilterComponent';
import SelectTag from '../components/SelectResumeScreenTag';
import { ResumeScreenListTab } from 'react-scale/components/GrowthCenter/constants';
import SAlert from 'react-s-alert';
import Link from '@scale/frontend-lib/link';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { HistoryTableModal } from 'react-scale/components/GrowthCenter/components/HistoryTableModal';
import ResumeSearchPage from 'react-scale/components/GrowthCenter/ResumeSearch/ResumeSearchPage';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import ResumeMetadataPage from 'react-scale/components/GrowthCenter/ResumeMetadata/ResumeMetadataPage';

const RESUME_SCREEN_LIST_COLUMNS = [
  {
    header: 'Job name',
    id: 'name',
  },
  {
    header: 'Type',
    id: 'type',
    enableSorting: true,
    cell: function TypeCell(props: any) {
      const text =
        props.getValue() === ScreenType.Greenhouse
          ? 'Greenhouse'
          : props.getValue() === ScreenType.RecruitingGreenhouse
          ? 'Recruiting'
          : 'OCP';
      return <div>{text}</div>;
    },
  },
  {
    header: 'Pod',
    id: 'podType',
    enableSorting: true,
    cell: function PodCell(props: any) {
      const text = props.getValue() === PodType.NotSet ? '' : props.getValue();
      return <div>{text}</div>;
    },
  },
  {
    header: 'Status',
    id: 'status',
    cell: function StatusCell(props: any) {
      const jobStatus = props.getValue();
      switch (jobStatus) {
        case ResumeScreeningConfigStatus.PAUSED:
          return (
            <div>
              <div>
                <Tag variant="neutral">Paused</Tag>
              </div>
            </div>
          );
        case ResumeScreeningConfigStatus.ACTIVE:
        default:
          return (
            <div>
              <div>
                <Tag variant="success">Active</Tag>
              </div>
            </div>
          );
      }
    },
  },
  {
    header: 'Actions',
    id: 'actions',
  },
];

const RESUME_SCREEN_JOB_LIST_COLUMNS = [
  {
    header: 'Job Name',
    id: 'name',
  },
  {
    header: 'Creator',
    id: 'creator',
  },
  {
    header: 'Actions',
    id: 'actions',
  },
];

const ResumeScreenList: React.FC<{
  allRootJobConfigs: ResumeScreeningConfig[];
  isListJobConfigsFetching: boolean;
  allResumeScreeningJobs: ResumeScreeningJob[];
  isListResumeScreeningJobsFetching: boolean;
  isRecruitingScreeningEnabled: boolean;
}> = ({
  allRootJobConfigs,
  isListJobConfigsFetching,
  allResumeScreeningJobs,
  isListResumeScreeningJobsFetching,
  isRecruitingScreeningEnabled,
}) => {
  const queryClient = useQueryClient();
  const [filteredJobConfigs, setFilteredJobConfigs] =
    useState<ResumeScreeningConfig[]>(allRootJobConfigs);

  const [filteredResumeScreeningJobs, setFilteredResumeScreeningJobs] =
    useState<ResumeScreeningJob[]>(allResumeScreeningJobs);

  const [searchTerm, setSearchTerm] = useState<string>('');

  const [searchJobTerm, setSearchJobTerm] = useState<string>('');

  const [filterTerms, setFilterTerms] = React.useState<{ value: string; label: string }[]>([]);

  const [currentTab, setCurrentTab] = useState<string>(
    _.first(Object.values(ResumeScreenListTab)) as string,
  );
  const onTabChange = (val: string) => {
    setCurrentTab(val as ResumeScreenListTab);
  };

  useEffect(() => {
    let filteredJobConfigs = allRootJobConfigs;

    if (filterTerms.length !== 0) {
      const types = filterTerms.map(term => term.value); // pod type and screen type
      filteredJobConfigs = filteredJobConfigs.filter(
        jobConfig =>
          types.includes(jobConfig.podType ? jobConfig.podType.toString() : PodType.NotSet) ||
          types.includes(jobConfig.type),
      );
    }

    if (searchTerm) {
      filteredJobConfigs = filteredJobConfigs.filter(jobConfig =>
        jobConfig.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredJobConfigs(filteredJobConfigs);
  }, [allRootJobConfigs, searchTerm, filterTerms]);

  useEffect(() => {
    if (searchJobTerm) {
      const filteredResumeScreeningJobs = allResumeScreeningJobs.filter(
        (jobConfig: ResumeScreeningJob) =>
          Object.values(jobConfig).some(
            (value: any) =>
              value && value.toString().toLowerCase().includes(searchJobTerm.toLowerCase()),
          ),
      );
      setFilteredResumeScreeningJobs(filteredResumeScreeningJobs);
    } else {
      setFilteredResumeScreeningJobs(allResumeScreeningJobs);
    }
  }, [allResumeScreeningJobs, searchJobTerm]);

  const deleteMutation = useMutation(
    (jobId: string) => {
      return deleteJobConfig(jobId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listJobConfigs', isRecruitingScreeningEnabled]);
      },
    },
  );

  const pauseMutation = useMutation(
    (id: string) => {
      const updateFields: UpdateJobConfigFields = {
        jobId: id,
        status: ResumeScreeningConfigStatus.PAUSED,
      };
      return updateJobConfig(updateFields);
    },
    {
      onSuccess: () => {
        SAlert.success('Job paused successfully');
        queryClient.invalidateQueries(['listJobConfigs', isRecruitingScreeningEnabled]);
      },
      onError: (error: any) => {
        SAlert.error(
          `Error pausing resume screening config: ${
            error.response.message ?? error.response.message.message
          }`,
        );
      },
    },
  );

  const activateMutation = useMutation(
    (id: string) => {
      const updateFields: UpdateJobConfigFields = {
        jobId: id,
        status: ResumeScreeningConfigStatus.ACTIVE,
      };
      return updateJobConfig(updateFields);
    },
    {
      onSuccess: () => {
        SAlert.success('Job activated successfully');
        queryClient.invalidateQueries(['listJobConfigs', isRecruitingScreeningEnabled]);
      },
      onError: (error: any) => {
        SAlert.error(
          `Error activating resume screening config: ${
            error.response.message ?? error.response.message.message
          }`,
        );
      },
    },
  );

  const deleteJobMutation = useMutation(
    (jobId: string) => {
      return deleteResumeScreeningJob(jobId);
    },
    {
      onSuccess: () => {
        SAlert.success('Job deleted successfully');
        queryClient.invalidateQueries(['listResumeScreeningJobs']);
      },
    },
  );

  const filteredJobConfigData: {
    name: JSX.Element;
    podType?: PodType;
    status: ResumeScreeningConfigStatus;
    actions: JSX.Element;
  }[] = filteredJobConfigs?.map((jobConfig: ResumeScreeningConfig) => {
    return {
      name: (
        <Link
          href={`${Routes.ResumeScreening}/${jobConfig._id}`}
          as={`/corp${Routes.ResumeScreening}/${jobConfig._id}`}
        >
          <a rel="noopener noreferrer">
            <Typography.Paragraph2>{jobConfig.name}</Typography.Paragraph2>
          </a>
        </Link>
      ),
      type: jobConfig.type,
      podType: jobConfig.podType,
      status: jobConfig.status,
      actions: (
        <div className="flex gap-2">
          <Link
            href={`${Routes.ResumeScreening}/${jobConfig._id}`}
            as={`/corp${Routes.ResumeScreening}/${jobConfig._id}`}
          >
            <a rel="noopener noreferrer">
              <Icon icon={['fas', 'edit']} size="sm" />
            </a>
          </Link>
          {(jobConfig.type === ScreenType.Greenhouse ||
            jobConfig.type === ScreenType.RecruitingGreenhouse) &&
            jobConfig.status !== ResumeScreeningConfigStatus.ACTIVE && (
              <Icon
                icon={['fas', 'play']}
                size="sm"
                tooltip="Activate"
                onClick={() => {
                  window.confirm(`Are you sure you want to activate job [${jobConfig.name}]?`) &&
                    activateMutation.mutate(jobConfig._id);
                }}
              />
            )}
          {(jobConfig.type === ScreenType.Greenhouse ||
            jobConfig.type === ScreenType.RecruitingGreenhouse) &&
            jobConfig.status !== ResumeScreeningConfigStatus.PAUSED && (
              <Icon
                icon={['fas', 'pause']}
                size="sm"
                tooltip="Pause"
                onClick={() => {
                  window.confirm(`Are you sure you want to pause job [${jobConfig.name}]?`) &&
                    pauseMutation.mutate(jobConfig._id);
                }}
              />
            )}
          <HistoryTableModal
            fetchEvents={async () => {
              const jobEvents = await getJobConfigEvents(jobConfig._id);
              const historyTableEvents = jobEvents.map(event => {
                return {
                  user: event.user.email,
                  operation: event.operationType,
                  timestamp: event.timestamp,
                  changes: event.data.changes,
                };
              });
              return historyTableEvents;
            }}
          />
          <Icon
            icon={['fas', 'trash-alt']}
            size="sm"
            onClick={() =>
              window.confirm(`Are you sure you want to delete ${jobConfig.name}?`) &&
              deleteMutation.mutate(jobConfig._id)
            }
          />
        </div>
      ),
    };
  });

  const filteredResumeScreeningJobData: {
    name: JSX.Element;
    creator: string;
    actions: JSX.Element;
  }[] = filteredResumeScreeningJobs?.map((job: ResumeScreeningJob) => {
    return {
      name: (
        <Link
          href={`${Routes.OneOffResumeScreening}/${job._id}`}
          as={`/corp${Routes.OneOffResumeScreening}/${job._id}`}
        >
          <a className="flex flex-row items-center" target="_blank" rel="noopener noreferrer">
            <Typography.Paragraph2>{job.name}</Typography.Paragraph2>
          </a>
        </Link>
      ),
      creator: job.createdBy,
      actions: (
        <div className="flex gap-2">
          <HistoryTableModal
            fetchEvents={async () => {
              const jobEvents = await getResumeScreeningJobEvents(job._id);
              const historyTableEvents = jobEvents.map(event => {
                return {
                  user: event.user.email,
                  operation: event.operationType,
                  timestamp: event.timestamp,
                  changes: event.data.changes,
                };
              });
              return historyTableEvents;
            }}
          />
          <Icon
            icon={['fas', 'trash-alt']}
            size="sm"
            onClick={() =>
              window.confirm(`Are you sure you want to delete ${job.name}?`) &&
              deleteJobMutation.mutate(job._id)
            }
          />
        </div>
      ),
    };
  });

  const tabs = Object.values(ResumeScreenListTab).map(tab => ({ id: tab }));

  return (
    <div className="flex flex-col h-full p-8 gap-4">
      <div className="flex flex-row justify-between">
        <Typography.H4>Resume Screening</Typography.H4>
        {currentTab === ResumeScreenListTab.ScreeningPipelines && (
          <Link href={`${Routes.ResumeScreeningSetup}`} as={`/corp${Routes.ResumeScreeningSetup}`}>
            <a rel="noopener noreferrer">
              <Button>
                <BaseIcon contrastText edge="start" icon={['fas', 'add']} variant="primary" />
                New Screening Pipeline
              </Button>
            </a>
          </Link>
        )}
        {currentTab === ResumeScreenListTab.ScreeningJobs && (
          <Link
            href={`${Routes.OneOffResumeScreening}`}
            as={`/corp${Routes.OneOffResumeScreening}`}
          >
            <a target="_blank" rel="noopener noreferrer">
              <Button>
                <BaseIcon contrastText edge="start" icon={['fas', 'add']} variant="primary" />
                New One-off Screening Job
              </Button>
            </a>
          </Link>
        )}
      </div>
      {isListJobConfigsFetching || isListResumeScreeningJobsFetching ? (
        <div className="flex flex-grow justify-center items-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <Tabs onChange={onTabChange} tabs={tabs} value={currentTab} variant="default" />
          {currentTab === ResumeScreenListTab.ScreeningPipelines && (
            <>
              <div className="flex justify-between">
                <TextField
                  className="w-[35%]"
                  leadingIcon="search"
                  id="search"
                  value={searchTerm}
                  onChange={(e: any) => {
                    setSearchTerm(e.target.value);
                  }}
                  placeholder="Search pipelines..."
                />

                <FilterComponent
                  filterMessage={
                    filterTerms.length === 0
                      ? 'Filter'
                      : filterTerms.map(term => term.label).join(', ')
                  }
                >
                  <SelectTag
                    showTagId={false}
                    value={filterTerms}
                    onChange={setFilterTerms}
                    multi={true}
                    placeholder={'Filter by pod or type'}
                  />
                </FilterComponent>
              </div>
              <Table
                columns={RESUME_SCREEN_LIST_COLUMNS}
                data={filteredJobConfigData}
                defaultPageSize={10}
                defaultRowHeight={40}
              />
            </>
          )}
          {currentTab === ResumeScreenListTab.ScreeningJobs && (
            <>
              <TextField
                className="w-[35%]"
                leadingIcon="search"
                id="search"
                value={searchJobTerm}
                onChange={(e: any) => {
                  setSearchJobTerm(e.target.value);
                }}
                placeholder="Search jobs by job name, creator..."
              />
              <Table
                columns={RESUME_SCREEN_JOB_LIST_COLUMNS}
                data={filteredResumeScreeningJobData}
                defaultPageSize={10}
                defaultRowHeight={40}
              />
            </>
          )}
          {currentTab === ResumeScreenListTab.ResumeSearch && <ResumeSearchPage />}
          {currentTab === ResumeScreenListTab.ResumeMetadata && <ResumeMetadataPage />}
        </>
      )}
    </div>
  );
};

export const ResumeScreeningPage: React.FC = () => {
  const isRecruitingScreeningEnabled = useUnleashFeatureFlag(
    'growth-resume-screen-recruiting',
    {},
    false,
  );

  const { data: allJobConfigs, isLoading: isListJobConfigsFetching } = useQuery(
    ['listJobConfigs', isRecruitingScreeningEnabled],
    () => listJobConfigs(isRecruitingScreeningEnabled ? undefined : ScreenFilter.Growth), // if isRecruitingScreeningEnabled is true, we want to fetch all job configs
    {
      refetchOnWindowFocus: false,
    },
  );
  const allRootJobConfigs = useMemo(
    () => allJobConfigs?.filter(config => config.rootConfig),
    [allJobConfigs],
  );

  const { data: allResumeScreeningJobs, isLoading: isListResumeScreeningJobsFetching } = useQuery(
    'listResumeScreeningJobs',
    listResumeScreeningJobs,
    {
      refetchOnWindowFocus: false,
    },
  );

  return (
    <div className="flex flex-col h-full pt-0">
      <ResumeScreenList
        allRootJobConfigs={allRootJobConfigs ?? []}
        isListJobConfigsFetching={isListJobConfigsFetching}
        allResumeScreeningJobs={allResumeScreeningJobs ?? []}
        isListResumeScreeningJobsFetching={isListResumeScreeningJobsFetching}
        isRecruitingScreeningEnabled={isRecruitingScreeningEnabled || false}
      />
    </div>
  );
};
